import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { HttpStatus } from "pages/Core";
import notistack from "pages/Core/Notistack";
import { SubCategoryDataModel } from "../Model/SubCategoryModal";
import {
  updateSubCategory,
  GetCategorySelectList,
} from "../services/SubCategoryservises";

interface UpdateSubCategoryProp {
  onUpdateSuccess(): void;
  subCategoryDetails: SubCategoryDataModel | null;
  closeModal(): void;
}

const UpdateSubCategory: React.FC<UpdateSubCategoryProp> = ({
  subCategoryDetails,
  onUpdateSuccess,
  closeModal,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SubCategoryDataModel>({ mode: "onBlur" });

  const [categoryList, setCategoryList] = useState<
    { value: string; text: string }[]
  >([]);

  const fetchCategories = async () => {
    try {
      const res: any = await GetCategorySelectList();
      if (res.status === HttpStatus.Success) {
        const formatted = res.data.map((c: any) => ({
          value: Number(c.value),
          text: c.text,
        }));
        setCategoryList(formatted);
      } else {
        notistack.notifyFromApi(res);
      }
    } catch (error: any) {
      notistack.error(error.message);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [])

  useEffect(() => {
    if (subCategoryDetails && categoryList.length > 0) {
      reset({
        id: subCategoryDetails.id,
        name: subCategoryDetails.name,
        categoryId: Number(subCategoryDetails.categoryId),
      });
    }
  }, [subCategoryDetails, categoryList, reset]);

  const onSubmit = async (data: SubCategoryDataModel) => {
    try {
      const response = await updateSubCategory(data);
      if (response.status === HttpStatus.Success) {
        notistack.notifyFromApi(response);
        onUpdateSuccess();
        closeModal();
      } else {
        notistack.notifyFromApi(response);
      }
    } catch (error: any) {
      notistack.error(error.message || "Something went wrong");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="row">
        {/* Category Dropdown */}
        <div className="col-md-6 mb-3">
          <label className="form-label">
            Select Category <span className="text-danger">*</span>
          </label>
          <select
            className={`form-control ${errors.categoryId ? "is-invalid" : ""}`}
            {...register("categoryId", {
              required: "Category is required",
              valueAsNumber: true,
            })}
          >
            <option value="">-- Select Category --</option>
            {categoryList.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.text}
              </option>
            ))}
          </select>
          {errors.categoryId && (
            <div className="text-danger small mt-1">
              {errors.categoryId.message}
            </div>
          )}
        </div>

        {/* SubCategory Name */}
        <div className="col-md-6 mb-3">
          <label className="form-label">
            SubCategory <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            maxLength={30}
            placeholder="Enter subcategory"
            className={`form-control ${errors.name ? "is-invalid" : ""}`}
            {...register("name", {
              required: "SubCategory is required",
              pattern: {
                value: /^(?!\s)([A-Za-z]+(\s{1,}[A-Za-z]+)*)$/,
                message:
                  "Only letters and spaces are allowed. No leading or trailing spaces.",
              },
            })}
          />
          {errors.name && (
            <div className="text-danger small mt-1">{errors.name.message}</div>
          )}
        </div>
      </div>

      <div className="mt-3 text-end">
        <button
          type="button"
          className="btn add-btn me-2"
          onClick={closeModal}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn submit-btn"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Updating..." : "Update"}
        </button>
      </div>
    </form>
  );
};

export default UpdateSubCategory;
